import { GALLERY_CONFIG } from '../gallery-config.js';
import { fetchCollectionProducts, transformProduct } from '../services/api/product-api.js';
import { filterByOrientation } from '../services/product/product-utils.js';

/**
 * Loading progress steps configuration
 */
const LOADING_STEPS = {
  START: 10,
  FETCH_START: 20,
  FETCH_COMPLETE: 45,
  TRANSFORM_COMPLETE: 60,
  PRELOAD_START: 72,
  PRELOAD_COMPLETE: 88,
  FINAL: 100
};

/**
 * Image preloading configuration
 */
const PRELOAD_CONFIG = {
  WIDTHS: [630, 872, 150],
  FINAL_DELAY: 400,
  DEFAULT_FRAME: 'Black',
  DEFAULT_MOUNT: 'Border'
};

/**
 * Data loading and preparation logic for the gallery builder component.
 */
export const GalleryBuilderData = {
  /**
   * Loads collection products and initializes the gallery builder
   * @async
   * @returns {Promise<void>}
   * @throws {Error} When product loading or initialization fails
   */
  async loadCollectionProducts() {
    this._validateRequiredContext();
    this._startLoadingSequence();

    try {
      // Step 1: Fetch raw products
      this._updateProgress(LOADING_STEPS.START);
      this._updateProgress(LOADING_STEPS.FETCH_START);
      
      const collectionHandle = this._getCollectionHandle();
      const rawProducts = await this._fetchProducts(collectionHandle);
      this._updateProgress(LOADING_STEPS.FETCH_COMPLETE);

      // Step 2: Transform products
      const transformedProducts = this._transformProducts(rawProducts);
      this._updateProgress(LOADING_STEPS.TRANSFORM_COMPLETE);

      // Step 3: Update stores
      this._updateProductStores(transformedProducts);
      
      // Step 4: Initialize default selection if needed
      const restored = await this._restoreGalleryFromPendingState(transformedProducts);

      if (!restored && this._shouldInitializeDefaultSelection()) {
        this.initializeDefaultSelection();
      }

      // Step 5: Preload images
      this._updateProgress(LOADING_STEPS.PRELOAD_START);
      await this._preloadGalleryImages();
      this._updateProgress(LOADING_STEPS.PRELOAD_COMPLETE);
      this._updateProgress(LOADING_STEPS.FINAL);

      // Step 5.5: Generate first randomize queue (while loading screen is still up)
      await this._generateInitialRandomizeQueue();

      // Step 6: Final delay for smooth transition
      await this._finalizeLoading();

    } catch (error) {
      this._handleLoadingError('loadCollectionProducts', error);
    } finally {
      await this._completeLoadingSequence();
    }
  },

  // ============================================================================
  // PRIVATE HELPER METHODS
  // ============================================================================

  /**
   * @private
   * @throws {Error} When required context is missing
   */
  _validateRequiredContext() {
    if (!this.$el) {
      throw new Error('GalleryBuilderData: $el context is required');
    }
    if (!this.$store) {
      throw new Error('GalleryBuilderData: $store context is required');
    }
  },

  /**
   * @private
   */
  _startLoadingSequence() {
    this.startLoadingScreen();
    this.$store.galleryGlobal.setLoading(true);
    this.$store.galleryCore.gallery_visible = false;
  },

  /**
   * Updates loading progress with validation
   * @private
   * @param {number} progress - Progress value (0-100)
   */
  _updateProgress(progress) {
    if (typeof progress !== 'number' || progress < 0 || progress > 100) {
      console.warn('Invalid progress value:', progress);
      return;
    }
    this.updateLoadingProgress(progress);
  },

  /**
   * @private
   * @returns {string} Collection handle
   */
  _getCollectionHandle() {
    return this.$el.dataset.collectionHandle || 'all';
  },

  /**
   * @private
   * @param {string} collectionHandle - Collection handle to fetch
   * @returns {Promise<Array>} Raw product data
   * @throws {Error} When fetch fails
   */
  async _fetchProducts(collectionHandle) {
    try {
      return await fetchCollectionProducts(collectionHandle);
    } catch (error) {
      throw new Error(`Failed to fetch products for collection '${collectionHandle}': ${error.message}`);
    }
  },

  /**
   * @private
   * @param {Array} rawProducts - Raw product data from API
   * @returns {Array} Transformed products
   */
  _transformProducts(rawProducts) {
    if (!Array.isArray(rawProducts)) {
      console.warn('Raw products is not an array:', rawProducts);
      return [];
    }

    return rawProducts
      .map((product) => transformProduct(product))
      .filter((product) => product !== null);
  },

  /**
   * @private
   * @param {Array} transformedProducts - Transformed product data
   */
  _updateProductStores(transformedProducts) {
    this.$store.galleryProducts.updateProducts(transformedProducts);
    this.$store.galleryGlobal.setCollectionsLoaded(true);
  },

  async _restoreGalleryFromPendingState(transformedProducts) {
    if (typeof this.hasPendingShareState !== 'function' || !this.hasPendingShareState()) {
      return false;
    }

    const pendingState = this.consumePendingShareState?.();
    if (!pendingState) {
      this.markShareStateApplied?.(false);
      return false;
    }

    const restored = this._hydrateShareState?.(pendingState, transformedProducts) ?? false;

    if (typeof this.markShareStateApplied === 'function') {
      this.markShareStateApplied(restored);
    }

    if (!restored && typeof this.clearPendingShareState === 'function') {
      this.clearPendingShareState();
    }

    return restored;
  },

  _hydrateShareState(shareState, products) {
    const core_store = this.$store?.galleryCore;
    const variants_store = this.$store?.galleryVariants;

    if (!core_store || !Array.isArray(products) || products.length === 0 || !shareState) {
      return false;
    }

    const missingArtworks = new Set();

    const productMap = new Map(
      products
        .filter(product => product && product.id !== undefined && product.id !== null)
        .map(product => [String(product.id), product])
    );

    const resolveArtworkList = (entries) => {
      if (!Array.isArray(entries)) return [];

      return entries.reduce((accumulator, entry) => {
        if (!entry || entry.id === undefined || entry.id === null) {
          return accumulator;
        }

        const product = productMap.get(String(entry.id));
        if (!product) {
          missingArtworks.add(String(entry.id));
          return accumulator;
        }

        if (entry.selected_variant && typeof entry.selected_variant === 'object') {
          const variant = {
            ...entry.selected_variant,
            product_id: product.id
          };

          product.selected_variant = variant;
          product.preview_variant = variant;
          product.variant_selected = true;
        }

        accumulator.push(product);
        return accumulator;
      }, []);
    };

    const orientationKeys = ['horizontal', 'vertical'];
    const targetOrientation = shareState.orientation === 'vertical' ? 'vertical' : 'horizontal';
    const layout_count = shareState.layout_count === 3 ? 3 : 2;

    const orientationResolved = orientationKeys.reduce((accumulator, key) => {
      const state = shareState.orientation_states?.[key] || {};
      const layout = state.layout === 3 ? 3 : 2;
      const artworks = resolveArtworkList(state.selected_artworks).slice(0, layout);

      accumulator[key] = {
        layout,
        artworks
      };

      return accumulator;
    }, {});

    orientationKeys.forEach((key) => {
      const state = orientationResolved[key];
      if (!state) return;

      const currentArtworks = state.artworks.filter(artwork => artwork && artwork.id);
      const needed = Math.max(0, state.layout - currentArtworks.length);

      if (needed > 0) {
        const existingIds = new Set(currentArtworks.map(artwork => String(artwork.id)));
        const candidates = filterByOrientation(products, key).filter(product => !existingIds.has(String(product.id)));
        const extras = candidates.slice(0, needed);
        state.artworks = [...currentArtworks, ...extras].slice(0, state.layout);
      } else {
        state.artworks = currentArtworks.slice(0, state.layout);
      }
    });

    let activeArtworks = resolveArtworkList(shareState.active_selection)
      .filter(artwork => artwork && artwork.id)
      .slice(0, layout_count);

    if (activeArtworks.length < layout_count) {
      const fallback = orientationResolved[targetOrientation]?.artworks || [];
      activeArtworks = fallback.slice(0, layout_count);
    }

    const targetOrientationResolved = orientationResolved[targetOrientation] || { layout: layout_count, artworks: [] };
    const validTargetCount = (targetOrientationResolved.artworks || []).filter(Boolean).length;

    if (!targetOrientationResolved.artworks || targetOrientationResolved.artworks.length < layout_count) {
      if (missingArtworks.size > 0) {
        console.warn('[GalleryBuilder] Missing artworks while restoring share state:', Array.from(missingArtworks));
      }
      return false;
    }

    if (activeArtworks.length < layout_count) {
      if (missingArtworks.size > 0) {
        console.warn('[GalleryBuilder] Active selection incomplete; missing artworks:', Array.from(missingArtworks));
      }
      return false;
    }

    const validActiveCount = activeArtworks.filter(Boolean).length;

    if (validTargetCount === 0 || validActiveCount === 0) {
      console.warn('[GalleryBuilder] Share state restore skipped due to empty selection for orientation:', targetOrientation);
      return false;
    }


    core_store.setAutoSaveEnabled?.(false);

    orientationKeys.forEach((key) => {
      const { layout, artworks } = orientationResolved[key] || { layout: 2, artworks: [] };
      const limitedArtworks = artworks.slice(0, layout);

      if (core_store.orientation_states[key]) {
        core_store.orientation_states[key].layout = layout;
        core_store.orientation_states[key].selected_artworks = [...limitedArtworks];

        if (typeof core_store.setSavedThirdArtwork === 'function') {
          const savedThird = limitedArtworks[2] ?? null;
          core_store.setSavedThirdArtwork(savedThird, key);
        }
      }
    });

    core_store.orientation = targetOrientation;
    core_store.layout_count = layout_count;
    core_store.selected_artworks = [...activeArtworks];

    const thirdVisible = layout_count === 3 && shareState.third_artwork_visible && activeArtworks.filter(Boolean).length >= 3;
    core_store.third_artwork_visible = thirdVisible;

    if (shareState.variant_selection) {
      const { size = null, frame = null, mount = null, locked = false } = shareState.variant_selection;
      core_store.variant_selection.size = size;
      core_store.variant_selection.frame = frame;
      core_store.variant_selection.mount = mount;
      core_store.variant_selection.locked = !!locked;
    }

    if (variants_store && typeof variants_store.applyVariantsToRestoredArtworks === 'function') {
      variants_store.applyVariantsToRestoredArtworks({ ...core_store.variant_selection });
    }

    core_store.setAutoSaveEnabled?.(true);

    return true;
  },

  /**
   * @private
   * @returns {boolean} True if default selection is needed
   */
  _shouldInitializeDefaultSelection() {
    return this.selected_artworks.length === 0;
  },

  /**
   * @private
   * @returns {Promise<void>}
   */
  async _preloadGalleryImages() {
    return await this.preloadGalleryImages();
  },

  /**
   * Generates the initial randomize queue during page load
   * This ensures the first randomize is instant
   * @private
   * @returns {Promise<void>}
   */
  async _generateInitialRandomizeQueue() {
    try {
      const queue_store = this.$store?.galleryRandomizeQueue;
      if (queue_store) {
        await queue_store.generateNextRandomSelection();
      }
    } catch (error) {
      console.warn('Failed to generate initial randomize queue:', error);
    }
  },

  /**
   * Finalizes loading with a delay for smooth transition
   * @private
   * @returns {Promise<void>}
   */
  async _finalizeLoading() {
    return new Promise((resolve) => 
      setTimeout(resolve, PRELOAD_CONFIG.FINAL_DELAY)
    );
  },

  /**
   * @private
   * @param {string} method - Method name where error occurred
   * @param {Error} error - Error object
   */
  _handleLoadingError(method, error) {
    console.error(`GalleryBuilderData.${method} failed:`, error);
    this.handleError(method, error);
  },

  /**
   * @private
   * @returns {Promise<void>}
   */
  async _completeLoadingSequence() {
    await this.stopLoadingScreen();
    this.$store.galleryCore.gallery_visible = true;
    this.$store.galleryGlobal.setLoading(false);
    this.initializeSwymWishlist();
  },

  // ============================================================================
  // PUBLIC METHODS
  // ============================================================================

  /**
   * Preloads gallery images for improved performance
   * @async
   * @returns {Promise<void>}
   */
  async preloadGalleryImages() {
    try {
      const store = this.$store.galleryCore;
      
      await this._preloadOrientationImages(store);
      await this._preloadVariantImages(store);
      
    } catch (error) {
      console.error('Failed to preload gallery images:', error);
    }
  },

  /**
   * @private
   * @param {Object} store - Gallery core store
   * @returns {Promise<void>}
   */
  async _preloadOrientationImages(store) {
    const preloadUrls = [];
    const horizontalArtworks = this._getOrientationArtworks(store, 'horizontal');
    const verticalArtworks = this._getOrientationArtworks(store, 'vertical');

    // Collect horizontal artwork URLs
    horizontalArtworks.forEach((artwork) => {
      this._addImageUrls(preloadUrls, artwork, ['horizontal_image_url', 'thumbnail_url']);
    });

    // Collect vertical artwork URLs (avoid duplicates)
    verticalArtworks.forEach((artwork) => {
      this._addImageUrls(preloadUrls, artwork, ['vertical_image_url']);
      
      // Add thumbnail only if not already included from horizontal
      if (!horizontalArtworks.includes(artwork) && artwork?.thumbnail_url) {
        preloadUrls.push(artwork.thumbnail_url);
      }
    });

    if (preloadUrls.length > 0) {
      await store.preloadImageBatch(preloadUrls, store.orientation);
    }
  },

  /**
   * @private
   * @param {Object} store - Gallery core store
   * @returns {Promise<void>}
   */
  async _preloadVariantImages(store) {
    const allArtworks = this._getAllUniqueArtworks(store);
    
    if (allArtworks.length === 0) return;

    store.initVariantManager();
    const options = store.variant_manager?.getCommonOptions(allArtworks);

    if (!options) return;

    const variantUrls = this._generateVariantUrls(allArtworks, store, options);
    
    if (variantUrls.length > 0) {
      await store.preloadImageBatch(variantUrls, store.orientation);
    }
  },

  /**
   * @private
   * @param {Object} store - Gallery core store
   * @param {string} orientation - Orientation ('horizontal' or 'vertical')
   * @returns {Array} Artworks for the orientation
   */
  _getOrientationArtworks(store, orientation) {
    const artworks = store.orientation_states[orientation]?.selected_artworks || [];
    return artworks.slice(0, GALLERY_CONFIG.LAYOUT.TRIO_COUNT);
  },

  /**
   * @private
   * @param {Object} store - Gallery core store
   * @returns {Array} All unique artworks
   */
  _getAllUniqueArtworks(store) {
    const horizontalArtworks = this._getOrientationArtworks(store, 'horizontal');
    const verticalArtworks = this._getOrientationArtworks(store, 'vertical');
    
    return [...new Set([...horizontalArtworks, ...verticalArtworks])].filter(Boolean);
  },

  /**
   * @private
   * @param {Array} preloadUrls - Array to add URLs to
   * @param {Object} artwork - Artwork object
   * @param {Array<string>} urlProperties - Properties to extract URLs from
   */
  _addImageUrls(preloadUrls, artwork, urlProperties) {
    urlProperties.forEach(property => {
      const url = artwork?.[property];
      if (url && !preloadUrls.includes(url)) {
        preloadUrls.push(url);
      }
    });
  },

  /**
   * @private
   * @param {Array} artworks - Artworks to generate URLs for
   * @param {Object} store - Gallery core store
   * @param {Object} options - Variant options
   * @returns {Array<string>} Generated URLs
   */
  _generateVariantUrls(artworks, store, options) {
    const variantUrls = [];
    const defaultFrame = this._getDefaultFrame(options);
    const defaultMount = this._getDefaultMount(options);

    if (!defaultFrame || !defaultMount) return variantUrls;

    artworks.forEach((artwork) => {
      if (!artwork?.variants) return;

      options.sizes.forEach((size) => {
        const variant = store.variant_manager.findVariant(
          artwork,
          size,
          defaultFrame,
          defaultMount
        );

        if (variant?.featured_image?.src) {
          const baseUrl = variant.featured_image.src.split('?')[0];
          PRELOAD_CONFIG.WIDTHS.forEach(width => {
            variantUrls.push(`${baseUrl}?width=${width}`);
          });
        }
      });
    });

    return variantUrls;
  },

  /**
   * @private
   * @param {Object} options - Variant options
   * @returns {string|null} Default frame
   */
  _getDefaultFrame(options) {
    return options.frames?.includes(PRELOAD_CONFIG.DEFAULT_FRAME) 
      ? PRELOAD_CONFIG.DEFAULT_FRAME 
      : options.frames?.[0] || null;
  },

  /**
   * @private
   * @param {Object} options - Variant options
   * @returns {string|null} Default mount
   */
  _getDefaultMount(options) {
    return options.mounts?.includes(PRELOAD_CONFIG.DEFAULT_MOUNT) 
      ? PRELOAD_CONFIG.DEFAULT_MOUNT 
      : options.mounts?.[0] || null;
  }
};
